const DEFAULT_COLOR = 0x000000;

// CSS url() for a texture's backing image. HTMLImageElement resources return
// their src directly; ImageBitmap resources are drawn to a canvas once and the
// data URL is cached per texture source.
const cssUrlCache = new WeakMap();
export const textureCssUrl = texture => {
    const source = texture.source;
    const resource = source.resource;
    if (resource && typeof resource.src === 'string') return resource.src;
    let url = cssUrlCache.get(source);
    if (!url) {
        const canvas = document.createElement('canvas');
        canvas.width = resource.width;
        canvas.height = resource.height;
        canvas.getContext('2d').drawImage(resource, 0, 0);
        url = canvas.toDataURL();
        cssUrlCache.set(source, url);
    }
    return url;
};

export const normalizeAlpha = alpha => {
    const normalized = Number(alpha);
    return Number.isFinite(normalized) ? Math.min(Math.max(normalized, 0), 1) : 1;
};

export const burnTintFromAmount = burn => {
    const numericBurn = Number(burn);
    const normalizedBurn = Number.isFinite(numericBurn)
        ? Math.min(Math.max(numericBurn, 0), 1)
        : 0;
    const channel = Math.round((1 - 0.7 * normalizedBurn) * 255);

    return channel * 0x010101;
};

export const normalizeColor = color => {
    if (typeof color === 'number') return Number.isFinite(color) ? color : DEFAULT_COLOR;
    if (color === null || color === undefined) return DEFAULT_COLOR;

    if (typeof color !== 'string') return color;

    const value = color.trim();
    if (!value || value.toLowerCase() === 'transparent') return DEFAULT_COLOR;

    if (value.startsWith('0x')) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : DEFAULT_COLOR;
    }

    if (value.startsWith('#')) {
        let hex = value.slice(1);
        if (hex.length === 3 || hex.length === 4) {
            hex = hex.slice(0, 3).split('').map(char => char + char).join('');
        } else if (hex.length >= 6) {
            hex = hex.slice(0, 6);
        }

        const parsed = parseInt(hex, 16);
        return Number.isFinite(parsed) ? parsed : DEFAULT_COLOR;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : DEFAULT_COLOR;
};

export const splitColorAlpha = (color, alpha = 1) => {
    let normalizedColor = color;
    let normalizedAlpha = normalizeAlpha(alpha);

    if (typeof color === 'string') {
        const value = color.trim();
        if (value.startsWith('#') && value.length > 7) {
            const parsedAlpha = parseInt(value.slice(7, 9), 16);
            if (Number.isFinite(parsedAlpha)) {
                normalizedAlpha = Math.max(parsedAlpha / 255, 1 / 255) * normalizedAlpha;
            }
            normalizedColor = value.slice(0, 7);
        }
    }

    return {
        color: normalizeColor(normalizedColor),
        alpha: normalizeAlpha(normalizedAlpha),
    };
};

export const setStrokeStyle = (graphics, width, color, alpha = 1) => {
    graphics.setStrokeStyle({width, color: normalizeColor(color), alpha: normalizeAlpha(alpha)});
    return graphics;
};

export const setFillStyle = (graphics, color, alpha = 1) => {
    graphics.setFillStyle({color: normalizeColor(color), alpha: normalizeAlpha(alpha)});
    return graphics;
};

export const strokeRect = (graphics, x, y, width, height, lineWidth, color, alpha = 1) => {
    graphics.rect(x, y, width, height).stroke({width: lineWidth, color: normalizeColor(color), alpha: normalizeAlpha(alpha)});
    return graphics;
};

export const fillRect = (graphics, x, y, width, height, color, alpha = 1) => {
    graphics.rect(x, y, width, height).fill({color: normalizeColor(color), alpha: normalizeAlpha(alpha)});
    return graphics;
};

export const fillStrokeRect = (graphics, x, y, width, height, fillColor, fillAlpha, lineWidth, lineColor, lineAlpha = 1) => {
    graphics.rect(x, y, width, height)
        .fill({color: normalizeColor(fillColor), alpha: normalizeAlpha(fillAlpha)})
        .stroke({width: lineWidth, color: normalizeColor(lineColor), alpha: normalizeAlpha(lineAlpha)});
    return graphics;
};

export const strokeCircle = (graphics, x, y, radius, lineWidth, color, alpha = 1) => {
    graphics.circle(x, y, radius).stroke({width: lineWidth, color: normalizeColor(color), alpha: normalizeAlpha(alpha)});
    return graphics;
};

export const fillCircle = (graphics, x, y, radius, color, alpha = 1) => {
    graphics.circle(x, y, radius).fill({color: normalizeColor(color), alpha: normalizeAlpha(alpha)});
    return graphics;
};

export const fillStrokeCircle = (graphics, x, y, radius, fillColor, fillAlpha, lineWidth, lineColor, lineAlpha = 1) => {
    graphics.circle(x, y, radius)
        .fill({color: normalizeColor(fillColor), alpha: normalizeAlpha(fillAlpha)})
        .stroke({width: lineWidth, color: normalizeColor(lineColor), alpha: normalizeAlpha(lineAlpha)});
    return graphics;
};
